import ApiModel from '../../models/ApiModel'

/**
 * Store generic values in local storage.
 * The type can be any primitive or complex object conforming to JSON.
 * The primary reason for this abstractions is to ensure no storage key
 *  has to exist in more than one place in the source code.
 */
export class GenericStorage<T> {
    constructor(private key: string) {}

    store(o: T) {
        const data = JSON.stringify(o)
        localStorage.setItem(this.key, data)
    }

    read(): T | undefined {
        const data = localStorage.getItem(this.key)
        if (!data) return undefined
        try {
            return JSON.parse(data) as T
        } catch {
            return undefined
        }
    }

    remove() {
        localStorage.removeItem(this.key)
    }
}

/**
 * This storage class can handle a list of api models rather than generic data.
 * The `store` and `read` functions automatically maps the data to and from the given ApiModel.
 * Internally uses a GenericStorage.
 */
export class ApiModelListStorage<D, T extends ApiModel<D>> {
    // Use composition instead of inheritance for polymorphism,
    //  because signatures are not compatible between T and T[].
    private storage: GenericStorage<D[]>

    constructor(
        key: string,
        private model: new (data: D) => T
    ) {
        this.storage = new GenericStorage<D[]>(key)
    }

    store(a: T[]) {
        const data = a.map((t) => t.data)
        this.storage.store(data)
    }

    read(): T[] {
        const data = this.storage.read() ?? []
        if (!data) return []
        return data.map((d) => new this.model(d))
    }
}
